<div class="vlab research">
  <p class="muted sm rq-rule" *ngIf="rule">
    Lots that sold well above their valuation range ({{ rule }}). Findings are suggestions until reviewed — a review never changes a valuation.
  </p>

  <div class="seg rq-modes" role="tablist" aria-label="Research queue mode">
    <button type="button" role="tab" class="pill" [class.on]="mode === 'review'" [attr.aria-selected]="mode === 'review'" (click)="setMode('review')">Review findings</button>
    <button type="button" role="tab" class="pill" [class.on]="mode === 'batch'" [attr.aria-selected]="mode === 'batch'" (click)="setMode('batch')">
      Batch entry<span *ngIf="dirtyCount"> · {{ dirtyCount }} unsaved</span>
    </button>
  </div>

  <!-- ================= batch entry ================= -->
  <section class="vl-card rq-batch" *ngIf="mode === 'batch'" aria-label="Batch entry">
    <div class="card-hd wrap">
      <h3>Why did these sell high? <span class="muted sm" *ngIf="batchTotal">{{ batchTotal | number }} waiting</span></h3>
      <span class="muted xs kbd-hint hide-phone">Tab / Shift+Tab: next / previous box · Alt+K: don't know · Ctrl/⌘+Enter: save batch</span>
    </div>
    <p class="muted sm b-intro">
      Type what each plate means to a buyer. Plates you can't explain: tick “Don't know” and they go to AI research.
      Empty rows are skipped. Your notes are kept in this browser until you save.
    </p>

    <div class="b-bar">
      <button type="button" class="btn-dark" [disabled]="!dirtyCount || batchSaving" (click)="saveBatch()">
        {{ batchSaving ? 'Saving…' : 'Save batch' + (dirtyCount ? ' (' + dirtyCount + ')' : '') }}
      </button>
      <span class="saved" *ngIf="batchResult" role="status">{{ batchResult }}</span>
      <span class="err-t xs" *ngIf="batchSaveError" role="alert">{{ batchSaveError }}</span>
      <span class="muted xs" *ngIf="dirtyOffPage">{{ dirtyOffPage }} unsaved on other pages (included in the save)</span>
    </div>

    <div *ngIf="batchError" class="vl-banner err slim" role="alert">
      <span>{{ batchError }}</span>
      <button type="button" class="btn-ghost" (click)="loadBatch(batchPage)">Retry</button>
    </div>
    <div *ngIf="batchLoading && !batch.length" class="muted sm pad">Loading the next lots…</div>
    <div *ngIf="!batchLoading && !batchError && !batch.length" class="muted sm pad">Nothing left to explain — every queued lot has a note, a finding or a research request.</div>

    <ol class="b-rows" *ngIf="batch.length" [class.loading]="batchLoading">
      <li class="b-row" *ngFor="let it of batch; let i = index; trackBy: trackItem" [class.has]="drafts[it.plate]?.why?.trim() || drafts[it.plate]?.dontKnow">
        <div class="b-lot">
          <span class="rq-rank num">#{{ it.rank }}</span>
          <span class="pchip plate-font">{{ it.plate }}</span>
          <span class="b-nums">
            <b class="num">{{ gbp(it.all_in_gbp) }}</b>
            <span class="muted xs">{{ it.house_label || '—' }}<span *ngIf="it.sold_date"> · {{ date(it.sold_date) }}</span></span>
          </span>
          <span class="b-nums">
            <span class="num">{{ range(it, true) }}</span>
            <span class="muted xs">{{ engineLabel(it) }} range</span>
          </span>
          <span class="b-nums">
            <b class="num err-under">{{ ratio(it.ratio) }}</b>
            <span class="muted xs">{{ gbpShort(it.excess_gbp) }} above</span>
          </span>
        </div>
        <div class="b-entry">
          <label class="sr" [attr.for]="'why-' + i">Why did {{ it.plate }} sell high?</label>
          <textarea [id]="'why-' + i" rows="2" [(ngModel)]="draft(it.plate).why" (ngModelChange)="onDraftChange()"
                    [disabled]="drafts[it.plate]?.dontKnow" (keydown)="onWhyKeydown($event, i, it.plate)"
                    placeholder="Why did it sell high? e.g. Ferrari F80 — F80 FER"></textarea>
          <div class="b-opts">
            <label class="sr" [attr.for]="'cat-' + i">Category for {{ it.plate }}</label>
            <select [id]="'cat-' + i" [(ngModel)]="draft(it.plate).category" (ngModelChange)="onDraftChange()" [disabled]="drafts[it.plate]?.dontKnow">
              <option value="">Category (optional)</option>
              <option *ngFor="let c of categories" [value]="c.id">{{ c.label }}</option>
            </select>
            <label class="dk">
              <input type="checkbox" [id]="'dk-' + i" [checked]="drafts[it.plate]?.dontKnow" (change)="toggleDontKnow(it.plate)"
                     (keydown)="onToggleKeydown($event, i, it.plate)" />
              <span>Don't know – send to AI research</span>
            </label>
          </div>
        </div>
      </li>
    </ol>

    <div class="b-foot" *ngIf="batch.length || batchPage > 1">
      <button type="button" class="btn-ghost" [disabled]="batchPage <= 1 || batchLoading" (click)="loadBatch(batchPage - 1)">← Previous</button>
      <span class="muted sm">Page {{ batchPage }} of {{ batchPages }}</span>
      <button type="button" class="btn-ghost" [disabled]="batchPage >= batchPages || batchLoading" (click)="loadBatch(batchPage + 1)">Next 20 →</button>
      <span class="grow"></span>
      <button type="button" class="btn-dark" [disabled]="!dirtyCount || batchSaving" (click)="saveBatch()">
        {{ batchSaving ? 'Saving…' : 'Save batch' + (dirtyCount ? ' (' + dirtyCount + ')' : '') }}
      </button>
    </div>
  </section>

  <section class="vl-split" aria-label="Research queue and item" [hidden]="mode !== 'review'">
    <div class="vl-card rq-list">
      <div class="card-hd wrap">
        <h3>Research queue <span class="muted sm" *ngIf="total">{{ total | number }} lots</span></h3>
        <div class="filters">
          <label class="sr" for="rq-status">Status</label>
          <select id="rq-status" [(ngModel)]="status" (ngModelChange)="onFilterChange()">
            <option value="">All statuses</option>
            <option *ngFor="let s of statuses" [value]="s.id">{{ s.label }}</option>
          </select>
          <label class="sr" for="rq-cat">Category</label>
          <select id="rq-cat" [(ngModel)]="category" (ngModelChange)="onFilterChange()">
            <option value="">All categories</option>
            <option *ngFor="let c of categories" [value]="c.id">{{ c.label }}</option>
          </select>
          <form class="q" (ngSubmit)="onFilterChange()">
            <label class="sr" for="rq-q">Find a plate</label>
            <input id="rq-q" name="q" [(ngModel)]="q" placeholder="Find plate" autocomplete="off" spellcheck="false" />
          </form>
        </div>
      </div>

      <div *ngIf="error" class="vl-banner err slim" role="alert">
        <span>{{ error }}</span>
        <button type="button" class="btn-ghost" (click)="load(false)">Retry</button>
      </div>
      <div *ngIf="loading && !items.length" class="muted sm pad">Loading the research queue…</div>
      <div *ngIf="!loading && !error && !items.length" class="muted sm pad">Nothing in the queue matches these filters.</div>

      <!-- desktop table -->
      <div class="tbl-scroll" *ngIf="items.length && !isPhone">
        <table class="stream rq-table">
          <thead>
            <tr>
              <th class="r">#</th>
              <th>Plate</th>
              <th>Sold</th>
              <th>Valuation range</th>
              <th>Over the top</th>
              <th>Reads as</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let it of items; trackBy: trackItem" [class.sel]="selected?.plate === it.plate">
              <td class="num r muted">{{ it.rank }}</td>
              <td class="rq-plate"><button type="button" class="pchip plate-font" (click)="select(it)" [attr.aria-label]="'Open research for ' + it.plate">{{ it.plate }}</button>
                <div><span class="spill" [ngClass]="statusClass(it)" [title]="findingErr(it) || ''">{{ statusLabel(it) }}</span></div></td>
              <td class="nowrap"><span class="num strong">{{ gbp(it.all_in_gbp) }}</span>
                <div class="sub">{{ it.house_label || '—' }}<span *ngIf="it.sold_date"> · {{ date(it.sold_date) }}</span></div></td>
              <td class="nowrap"><span class="num">{{ range(it, false) }}</span>
                <div class="sub">{{ engineLabel(it) }}<span *ngIf="it.stat_mid_gbp != null"> · stat {{ gbp(it.stat_mid_gbp) }}</span></div></td>
              <td class="nowrap"><span class="num strong err-under">{{ ratio(it.ratio) }}</span>
                <div class="sub">{{ gbp(it.excess_gbp) }} above</div></td>
              <td class="rq-reads">
                <ng-container *ngIf="it.review?.reads_as || findingOk(it)?.reads_as; else noReads">
                  <span class="reads-clamp" [title]="it.review?.reads_as || findingOk(it)?.reads_as">{{ it.review?.reads_as || findingOk(it)?.reads_as }}</span>
                  <div class="sub" *ngIf="it.review?.category || findingOk(it)?.category">{{ categoryLabel(it.review?.category || findingOk(it)?.category) }}</div>
                </ng-container>
                <ng-template #noReads><span class="muted">—</span></ng-template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- phone cards -->
      <div class="cards" *ngIf="items.length && isPhone">
        <button *ngFor="let it of items; trackBy: trackItem" type="button" class="lcard" [class.sel]="selected?.plate === it.plate" (click)="select(it)">
          <span class="lc-top">
            <span class="rq-rank num">#{{ it.rank }}</span>
            <span class="pchip plate-font">{{ it.plate }}</span>
            <span class="spill" [ngClass]="statusClass(it)">{{ statusLabel(it) }}</span>
          </span>
          <span class="lc-meta">{{ it.house_label || '—' }}<span *ngIf="it.sold_date"> · {{ date(it.sold_date) }}</span></span>
          <span class="lc-nums">
            <span><i>Sold</i><b class="strong">{{ gbp(it.all_in_gbp) }}</b></span>
            <span><i>Range · {{ engineLabel(it) }}</i><b>{{ range(it, true) }}</b></span>
          </span>
          <span class="lc-err"><span class="err-under">{{ ratio(it.ratio) }} over the top</span><span class="muted"> · {{ gbp(it.excess_gbp) }} above</span></span>
          <span class="lc-meta" *ngIf="it.review?.reads_as || findingOk(it)?.reads_as">Reads as <b>{{ it.review?.reads_as || findingOk(it)?.reads_as }}</b></span>
        </button>
      </div>

      <div class="more" *ngIf="items.length">
        <span class="muted sm">Showing {{ items.length | number }} of {{ total | number }}</span>
        <button type="button" class="btn-ghost" *ngIf="canLoadMore" (click)="load(true)">Load more</button>
        <span class="muted sm" *ngIf="loading && items.length">Loading…</span>
      </div>
    </div>

    <!-- ---------------- item panel ---------------- -->
    <aside class="vl-card vl-detail rq-item" aria-label="Research item" #itemPanel>
      <div *ngIf="!selected" class="placeholder">
        <b>Research item</b>
        <span class="muted sm">{{ isPhone ? 'Tap' : 'Click' }} a plate in the queue to see what it means and review the finding.</span>
      </div>

      <ng-container *ngIf="selected as it">
        <div class="d-hd">
          <span class="bigplate plate-font">{{ it.plate }}</span>
          <span class="d-house">
            #{{ it.rank }} · {{ it.house_label || '—' }}<span *ngIf="it.sold_date"> · {{ date(it.sold_date) }}</span>
            <span class="spill" [ngClass]="statusClass(it)">{{ statusLabel(it) }}</span>
          </span>
        </div>

        <div class="d-cards">
          <div class="dc">
            <div class="dc-l">All-in result</div>
            <div class="dc-v">{{ gbp(it.all_in_gbp) }}</div>
            <div class="dc-s">{{ it.style || '' }}</div>
          </div>
          <div class="dc">
            <div class="dc-l">Valuation range · {{ engineLabel(it) }}</div>
            <div class="dc-v">{{ range(it, false) }}</div>
            <div class="dc-s">central {{ gbp(it.mid_gbp) }}<span *ngIf="it.stat_mid_gbp != null"> · stat {{ gbp(it.stat_mid_gbp) }}</span></div>
          </div>
          <div class="dc">
            <div class="dc-l">Over the top of the range</div>
            <div class="dc-v err-under">{{ ratio(it.ratio) }}</div>
            <div class="dc-s">{{ gbp(it.excess_gbp) }} above</div>
          </div>
        </div>
        <button type="button" class="btn-ghost link-btn" *ngIf="it.lot_key" (click)="openInScorecard(it)">Open lot in scorecard →</button>

        <div *ngIf="itemError" class="vl-banner err slim" role="alert"><span>{{ itemError }}</span></div>

        <!-- finding -->
        <div class="d-sec rq-finding">
          <h4>Research finding</h4>
          <ng-container *ngIf="findingOk(it) as f; else noFinding">
            <span class="from-note" *ngIf="fromOwnerNote(it)">From your note · structured by Claude</span>
            <div class="reads">
              <span class="muted xs">Reads as</span>
              <span class="reads-v">{{ f.reads_as || '—' }}</span>
            </div>
            <div class="badges">
              <span class="badge-c" *ngIf="f.category">{{ categoryLabel(f.category) }}</span>
              <span class="badge-conf" *ngIf="f.confidence" [ngClass]="'conf-' + f.confidence">{{ f.confidence }} confidence</span>
            </div>
            <p class="rq-expl" *ngIf="f.explanation">{{ f.explanation }}</p>
            <div *ngIf="f.sources?.length">
              <h5>Sources</h5>
              <ul class="srcs">
                <li *ngFor="let s of f.sources">
                  <a *ngIf="safeUrl(s.url) as u" [href]="u" target="_blank" rel="noopener noreferrer">{{ s.title || host(u) }}</a>
                  <span class="muted xs" *ngIf="safeUrl(s.url) as u"> · {{ host(u) }}</span>
                  <span *ngIf="!safeUrl(s.url)">{{ s.title || s.url }}</span>
                </li>
              </ul>
            </div>
            <div *ngIf="f.related_patterns?.length">
              <h5>Related patterns</h5>
              <div class="xchips"><span class="xchip" *ngFor="let p of f.related_patterns">{{ p }}</span></div>
            </div>
            <p class="muted xs" *ngIf="f.model || f.researched_at">
              {{ f.model }}<span *ngIf="f.researched_at"> · {{ dateTime(f.researched_at) }}</span><span *ngIf="f.searches != null"> · {{ f.searches }} searches</span>
            </p>
          </ng-container>
          <ng-template #noFinding>
            <div class="not-yet" *ngIf="!findingErr(it) && it.status === 'owner_explained'">
              <b>Your note is waiting for Claude</b>
              <span class="muted sm">Claude structures owner notes in batches; the finding appears here for you to accept.</span>
            </div>
            <div class="not-yet" *ngIf="!findingErr(it) && it.status === 'research_requested'">
              <b>Sent to AI research</b>
              <span class="muted sm">Research runs in batches; findings appear here for review.</span>
            </div>
            <div class="vl-banner err slim" *ngIf="findingErr(it)"><span>Research failed: {{ findingErr(it) }}</span></div>
            <div class="not-yet" *ngIf="!findingErr(it) && it.status !== 'owner_explained' && it.status !== 'research_requested'">
              <b>Not researched yet</b>
              <span class="muted sm">Research runs in batches; findings appear here for review.</span>
            </div>
          </ng-template>
          <div *ngIf="itemLoading" class="muted xs">Refreshing…</div>
        </div>

        <!-- owner's own note (batch entry) -->
        <div class="d-sec rq-owner" *ngIf="ownerNote(it) || it.research_requested || it.status === 'queued'">
          <div class="sec-hd">
            <h4>Your note</h4>
            <button type="button" class="btn-ghost sm" *ngIf="!noteEditing" (click)="startNoteEdit(it)">{{ ownerNote(it) ? 'Edit note' : 'Add a note' }}</button>
          </div>
          <ng-container *ngIf="!noteEditing">
            <blockquote class="onote" *ngIf="ownerNote(it) as n">
              “{{ n.why }}”
              <footer class="muted xs"><span *ngIf="n.category">{{ categoryLabel(n.category) }} · </span>{{ n.by }}<span *ngIf="n.at"> · {{ dateTime(n.at) }}</span></footer>
            </blockquote>
            <div class="muted sm" *ngIf="!ownerNote(it) && it.research_requested">
              Sent to AI research<span *ngIf="it.research_requested.at"> · {{ dateTime(it.research_requested.at) }}</span>
            </div>
            <div class="muted sm" *ngIf="!ownerNote(it) && !it.research_requested">No note yet.</div>
          </ng-container>
          <form class="note-edit" *ngIf="noteEditing" (ngSubmit)="saveNote(it)">
            <label class="sr" for="rq-note-why">Why did it sell high?</label>
            <textarea id="rq-note-why" name="noteWhy" rows="3" [(ngModel)]="noteWhy" placeholder="Why did it sell high? e.g. Ferrari F80 — F80 FER"></textarea>
            <label class="sr" for="rq-note-cat">Category</label>
            <select id="rq-note-cat" name="noteCategory" [(ngModel)]="noteCategory">
              <option value="">Category (optional)</option>
              <option *ngFor="let c of categories" [value]="c.id">{{ c.label }}</option>
            </select>
            <div class="rv-actions">
              <button type="submit" class="btn-dark" [disabled]="!noteWhy.trim() || noteSaving">{{ noteSaving ? 'Saving…' : 'Save note' }}</button>
              <button type="button" class="btn-ghost" [disabled]="noteSaving" (click)="saveNote(it, true)">Don't know – send to AI research</button>
              <button type="button" class="btn-ghost" [disabled]="noteSaving" (click)="cancelNoteEdit()">Cancel</button>
            </div>
          </form>
          <span class="saved" *ngIf="noteSaved" role="status">{{ noteSaved }}</span>
          <span class="err-t xs" *ngIf="noteError" role="alert">{{ noteError }}</span>
        </div>

        <!-- saved review -->
        <div class="d-sec" *ngIf="it.review as r">
          <h4>Review</h4>
          <div class="rv-saved">
            <span class="spill" [ngClass]="'rs-rev-' + r.decision">{{ decisionLabel(r.decision) }}</span>
            <span *ngIf="r.reads_as">reads as <b>{{ r.reads_as }}</b></span>
            <span class="muted" *ngIf="r.category">· {{ categoryLabel(r.category) }}</span>
          </div>
          <div *ngIf="r.note" class="rv-note">“{{ r.note }}”</div>
          <div class="muted xs">{{ r.by }}<span *ngIf="r.at"> · {{ dateTime(r.at) }}</span></div>
        </div>

        <!-- Claude's questions on an owner-note finding, right above the decision -->
        <div class="d-sec rq-questions" *ngIf="fromOwnerNote(it) && findingOk(it)?.questions?.length">
          <h4>Claude asks:</h4>
          <ul class="why"><li *ngFor="let q of findingOk(it).questions">{{ q }}</li></ul>
        </div>

        <!-- review form -->
        <form class="d-sec rv-form" (ngSubmit)="saveReview()">
          <h4>{{ it.review ? 'Review again' : 'Review' }}</h4>
          <div class="seg" role="group" aria-label="Decision">
            <button type="button" class="pill dec dec-acc" [class.on]="decision === 'accepted'" [attr.aria-pressed]="decision === 'accepted'" (click)="setDecision('accepted')">Accept</button>
            <button type="button" class="pill dec dec-rej" [class.on]="decision === 'rejected'" [attr.aria-pressed]="decision === 'rejected'" (click)="setDecision('rejected')">Reject</button>
            <button type="button" class="pill dec dec-more" [class.on]="decision === 'needs_more'" [attr.aria-pressed]="decision === 'needs_more'" (click)="setDecision('needs_more')">Needs more</button>
          </div>
          <div class="rv-fields">
            <div class="fld">
              <label for="rv-reads">Reads as&nbsp;<span class="muted">(optional)</span></label>
              <input id="rv-reads" name="readsAs" [(ngModel)]="readsAs" placeholder="e.g. Ferrari F80" autocomplete="off" />
            </div>
            <div class="fld">
              <label for="rv-cat">Category&nbsp;<span class="muted">(optional)</span></label>
              <select id="rv-cat" name="reviewCategory" [(ngModel)]="reviewCategory">
                <option value="">—</option>
                <option *ngFor="let c of categories" [value]="c.id">{{ c.label }}</option>
              </select>
            </div>
          </div>
          <div class="fld">
            <label for="rv-note">Note&nbsp;<span class="muted">(optional)</span></label>
            <textarea id="rv-note" name="note" rows="2" [(ngModel)]="note" placeholder="Anything the next research pass should know"></textarea>
          </div>
          <div class="rv-actions">
            <button type="submit" class="btn-dark" [disabled]="!decision || saving">{{ saving ? 'Saving…' : 'Save review' }}</button>
            <span class="saved" *ngIf="saved" role="status">Saved</span>
            <span class="err-t xs" *ngIf="saveError">{{ saveError }}</span>
          </div>
        </form>
      </ng-container>
    </aside>
  </section>
</div>
